import { useCallback, useMemo } from "react";

import {
  resolvePlaybackCanStart,
  resolvePlaybackCapabilities,
  resolvePlaybackSeekRange,
  useVideoTimeline,
  type PlaybackTransport,
} from "../Video";
import { consumePlaybackToggleShortcut } from "../Video/playbackShortcut";
import { normalizeMediaTimelineCurrent } from "./mediaTimelineMath";

export type MediaTimelineShortcutCommand =
  | "toggle"
  | "step"
  | "boundary"
  | "createMarker"
  | "removeMarker";

const ALL_SHORTCUT_COMMANDS: readonly MediaTimelineShortcutCommand[] = [
  "toggle",
  "step",
  "boundary",
  "createMarker",
  "removeMarker",
];

/**
 * Structural key event so one dispatcher accepts React synthetic and native keyboard events —
 * consumers attach it to whatever DOM scope they own.
 */
export interface MediaTimelineShortcutKeyEvent {
  code: string;
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  repeat: boolean;
  defaultPrevented: boolean;
  preventDefault: () => void;
  stopPropagation: () => void;
}

export interface UseMediaTimelineShortcutsOptions {
  transport: PlaybackTransport;
  /** Arrow-step size in seconds. Default 0.1 — the bar's own step default. */
  stepSeconds?: number;
  disabled?: boolean;
  /** Marker-creation intent; without it Shift+C passes through untouched. */
  onMarkerCreate?: (timeSeconds: number) => void;
  /** Marker-removal intent; Delete fires it with `removableMarkerId`. */
  onMarkerRemove?: (markerId: string) => void;
  /**
   * The one marker pinned exactly at the playhead, resolved reactively by the scope owner —
   * the bar pins a lone marker and resolves a same-time tie through the single active marker.
   * `null` — no marker there, or an unresolved tie — never guesses a target: Delete then
   * passes through unconsumed and stays unadvertised. Backspace is no alias (browser back
   * navigation and text-editing collisions).
   */
  removableMarkerId?: string | null;
  /** Commands to enable. Default: all. Excluded commands never consume their keys. */
  commands?: readonly MediaTimelineShortcutCommand[];
}

export interface MediaTimelineShortcuts {
  /** Returns whether the event was consumed. Consumed events stop propagation, so nesting the
   * bar inside a consumer scope wired with the same dispatcher never double-fires. */
  onKeyDown: (event: MediaTimelineShortcutKeyEvent) => boolean;
  /** Only the shortcuts that are actually active; mirror it onto the scope via aria-keyshortcuts. */
  ariaKeyShortcuts: string;
}

function readCurrent(transport: PlaybackTransport): number {
  const { currentTimeSeconds, durationSeconds } = transport.getSnapshot();
  return normalizeMediaTimelineCurrent(currentTimeSeconds, durationSeconds);
}

/**
 * Reusable timeline shortcut dispatcher: Space toggle, Arrow steps, Home/End boundary seeks,
 * Shift+C marker-creation intent, Delete marker-removal intent for the marker pinned at the
 * playhead. `MediaTimelineBar` runs this same hook on its slider and for its mounted Space
 * fallback; a consumer can additionally attach the returned handler to a wider scope (page,
 * studio root) for the identical contract.
 *
 * The hook never registers window/document listeners — which DOM scope the handler binds to is
 * the consumer's decision, and so are wider-scope exclusion policies (text entry, IME, modals).
 * Note the bar's own slider is an `input[type=range]`: it does not consume these keystrokes for
 * text, so blanket "skip all inputs" guards at the outer scope would wrongly swallow it.
 */
export function useMediaTimelineShortcuts({
  transport,
  stepSeconds = 0.1,
  disabled = false,
  onMarkerCreate,
  onMarkerRemove,
  removableMarkerId = null,
  commands = ALL_SHORTCUT_COMMANDS,
}: UseMediaTimelineShortcutsOptions): MediaTimelineShortcuts {
  const commandSet = useMemo(() => new Set(commands), [commands]);
  const toggleAvailable = useVideoTimeline(
    transport,
    (snapshot) => snapshot.playing || resolvePlaybackCanStart(snapshot),
  );

  const onKeyDown = useCallback(
    (event: MediaTimelineShortcutKeyEvent): boolean => {
      // A nested control may consume with preventDefault() alone and still let the event bubble;
      // re-handling it at an outer scope would double-fire the command.
      if (disabled || event.defaultPrevented) return false;

      if (commandSet.has("toggle") && consumePlaybackToggleShortcut(event, transport, disabled)) {
        return true;
      }

      if (
        commandSet.has("createMarker") &&
        onMarkerCreate !== undefined &&
        event.code === "KeyC" &&
        event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) onMarkerCreate(readCurrent(transport));
        return true;
      }

      if (
        commandSet.has("removeMarker") &&
        onMarkerRemove !== undefined &&
        removableMarkerId !== null &&
        event.key === "Delete" &&
        !event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) onMarkerRemove(removableMarkerId);
        return true;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return false;
      if (!resolvePlaybackCapabilities(transport.getSnapshot()).seek) return false;

      let nextTime: number | undefined;
      switch (event.key) {
        case "ArrowLeft":
        case "ArrowDown":
          if (commandSet.has("step")) nextTime = readCurrent(transport) - stepSeconds;
          break;
        case "ArrowRight":
        case "ArrowUp":
          if (commandSet.has("step")) nextTime = readCurrent(transport) + stepSeconds;
          break;
        case "Home":
          if (commandSet.has("boundary")) {
            nextTime = resolvePlaybackSeekRange(transport.getSnapshot()).startSeconds;
          }
          break;
        case "End":
          if (commandSet.has("boundary")) {
            nextTime = resolvePlaybackSeekRange(transport.getSnapshot()).endSeconds;
          }
          break;
      }
      if (nextTime === undefined) return false;

      event.preventDefault();
      event.stopPropagation();
      transport.seekToSeconds(nextTime);
      return true;
    },
    [
      commandSet,
      disabled,
      onMarkerCreate,
      onMarkerRemove,
      removableMarkerId,
      stepSeconds,
      transport,
    ],
  );

  // Arrows and Home/End ride the slider's native range semantics, so only the non-obvious
  // shortcuts are advertised — and only while their command is actually live.
  const ariaKeyShortcuts = useMemo(
    () =>
      disabled
        ? ""
        : [
            commandSet.has("toggle") && toggleAvailable ? "Space" : null,
            commandSet.has("createMarker") && onMarkerCreate !== undefined ? "Shift+C" : null,
            commandSet.has("removeMarker") &&
            onMarkerRemove !== undefined &&
            removableMarkerId !== null
              ? "Delete"
              : null,
          ]
            .filter((shortcut): shortcut is string => shortcut !== null)
            .join(" "),
    [commandSet, disabled, onMarkerCreate, onMarkerRemove, removableMarkerId, toggleAvailable],
  );

  return { onKeyDown, ariaKeyShortcuts };
}
